import type {DocumentNode} from "graphql";
import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    archiveContentAssignmentsType,
    createAssignmentsType,
    getClassSelectorType,
    getStudentsType,
    updateAssignmentType,
} from "./__graphql-types__/index.ts";

export const ASSIGNMENT_FRAGMENT: DocumentNode = gql`
    fragment AssignmentInfoFragment on Assignment {
        id
        contents {
            id
            title: translatedTitle
            kind
        }
        studentList {
            id
            cacheId
            name
            isK4dClassroom
            isChildAssignmentsClassroom
            signupCode
        }
        students {
            id
            kaid
        }
        startDate
        dueDate
        isDraft
        subjectSlug
        title
        instructions
    }
`;

export const ARCHIVE_ASSIGNMENTS_MUTATION: GqlOperation<
    archiveContentAssignmentsType["response"],
    archiveContentAssignmentsType["variables"]
> = gqlOp(gql`
    mutation archiveContentAssignments($assignmentIds: [ID]!) {
        archiveAssignments(ids: $assignmentIds) {
            assignments {
                ...AssignmentInfoFragment
            }
        }
    }
    ${ASSIGNMENT_FRAGMENT}
`);

// Exported for testing.
export const CLASS_SELECTOR_QUERY: GqlOperation<
    getClassSelectorType["response"],
    getClassSelectorType["variables"]
> = gqlOp(gql`
    query getClassSelector {
        coach: user {
            id
            studentLists: coachedStudentLists {
                id
                descriptor
                cacheId
                name
                signupCode
                countStudents
                hasKADAiGuideEnrolledStudents
                isK4dClassroom
            }
        }
    }
`);

export const STUDENT_SELECTOR_QUERY: GqlOperation<
    getStudentsType["response"],
    getStudentsType["variables"]
> = gqlOp(gql`
    query getStudents(
        $classDescriptor: String!
        $aiGuideEnabledStudentsOnly: Boolean!
    ) {
        classroom: classroomByDescriptor(descriptor: $classDescriptor) {
            id
            cacheId
            studentKaidsAndNicknames {
                id
                kaid
                coachNickname
            }
            students @include(if: $aiGuideEnabledStudentsOnly) {
                id
                kaid
                hasAccessToAIGuideLearner
                isAIGuideEnabled
            }
        }
    }
`);

const NEW_ASSIGNMENT_POST_BUTTON_ASSIGNMENT_FRAGMENT: DocumentNode = gql`
    fragment NewAssignmentPostButtonFragment on Assignment {
        id
        contents {
            id
            title: translatedTitle
            kind
        }
        studentList {
            id
            cacheId
            name
            isK4dClassroom
            isChildAssignmentsClassroom
            signupCode
        }
        students {
            id
            kaid
        }
        startDate
        dueDate
        isDraft
        subjectSlug
        title
        instructions
    }
`;

// NOTE: Exported for testing purposes
export const CREATE_ASSIGNMENTS_MUTATION: GqlOperation<
    createAssignmentsType["response"],
    createAssignmentsType["variables"]
> = gqlOp(gql`
    mutation createAssignments(
        $classDescriptors: [String!]!
        $qualifiedContentDescriptorsList: [String!]
        $startDate: DateTime!
        $dueDate: DateTime
        $isDraft: Boolean
        $studentKaids: [ID]
        $subjectSlug: String!
        $questionSetType: ExerciseItemPickerStrategy
        $title: String
        $instructions: String
        $configuredActivityInputs: String
        $creationKaLocale: String!
        $assignToAllStudents: Boolean
    ) {
        createAssignments(
            assignment: {
                classroomDescriptors: $classDescriptors
                qualifiedContentDescriptorsList: $qualifiedContentDescriptorsList
                startDate: $startDate
                dueDate: $dueDate
                isDraft: $isDraft
                studentKaids: $studentKaids
                subjectSlug: $subjectSlug
                exerciseItemPickerStrategy: $questionSetType
                title: $title
                instructions: $instructions
                configuredActivityInputs: $configuredActivityInputs
                creationKaLocale: $creationKaLocale
                assignToAllStudents: $assignToAllStudents
            }
        ) {
            assignments {
                ...NewAssignmentPostButtonFragment
            }
        }
    }

    ${NEW_ASSIGNMENT_POST_BUTTON_ASSIGNMENT_FRAGMENT}
`);

// NOTE: Exported for testing purposes
export const UPDATE_ASSIGNMENT_MUTATION: GqlOperation<
    updateAssignmentType["response"],
    updateAssignmentType["variables"]
> = gqlOp(gql`
    mutation updateAssignment(
        $assignmentId: ID!
        $studentKaids: [ID]
        $studentKaidsToAdd: [ID]
        $studentKaidsToRemove: [ID]
        $contentDescriptors: [String]
        $startDate: DateTime
        $dueDate: DateTime
        $isDraft: Boolean
    ) {
        updateAssignment(
            id: $assignmentId
            assignment: {
                studentKaids: $studentKaids
                contentDescriptors: $contentDescriptors
                startDate: $startDate
                dueDate: $dueDate
                isDraft: $isDraft
                studentKaidsToAdd: $studentKaidsToAdd
                studentKaidsToRemove: $studentKaidsToRemove
            }
        ) {
            assignment {
                ...NewAssignmentPostButtonFragment
            }
        }
    }

    ${NEW_ASSIGNMENT_POST_BUTTON_ASSIGNMENT_FRAGMENT}
`);
